import React, { useMemo } from 'react';
import type { ApexOptions } from 'apexcharts';
import { Alert, Spin, Typography } from 'antd';
import Chart from 'react-apexcharts';

import type { InspectAssessmentPeriodPrices } from '../../../../state/app/inspect_assessment/types';
import {
  assessmentPeriodMarketChanges,
  finiteOrNull,
  formatPrice,
  formatYield,
  pmChartPrice,
  relevantPeriodPriceSeries,
} from './chartData';
import type { AssessmentChartPoint } from './chartData';
import { formatAssessmentDate } from './formatters';
import PeriodChangeTag from './PeriodChangeTag';

type ChartPoint = AssessmentChartPoint;
type ChartSeries = {
  name: string;
  type: 'line';
  data: ChartPoint[];
};

type Props = {
  data?: InspectAssessmentPeriodPrices;
  error?: string;
  instrumentCode?: string;
  loading: boolean;
  periodLabel: string;
};

const InstrumentPriceChart: React.FC<Props> = ({
  data,
  error,
  instrumentCode,
  loading,
  periodLabel,
}) => {
  const chart = useMemo(() => {
    const priceSeries: ChartSeries[] = [];
    const yieldSeries: ChartSeries[] = [];
    const relevantAssets = relevantPeriodPriceSeries(data);
    const hasMultipleAssets = relevantAssets.length > 1;
    const changes = assessmentPeriodMarketChanges(data);

    relevantAssets.forEach(asset => {
      const baseName =
        asset.ticker || asset.name || `Asset #${asset.underlying_asset_id}`;
      const assetName = hasMultipleAssets
        ? `${baseName} #${asset.underlying_asset_id}`
        : baseName;
      const prices = asset.points.map(point => ({
        x: point.timestamp,
        y: pmChartPrice(point, asset.is_fx_forward),
      }));
      if (prices.some(point => point.y !== null)) {
        priceSeries.push({
          name: `${assetName} price`,
          type: 'line',
          data: prices,
        });
      }

      if (asset.is_bond) {
        const yields = asset.points.map(point => ({
          x: point.timestamp,
          y: finiteOrNull(point.ask_yield),
        }));
        if (yields.some(point => point.y !== null)) {
          yieldSeries.push({
            name: `${assetName} ask yield`,
            type: 'line',
            data: yields,
          });
        }
      }
    });

    return {
      hasBondSeries: relevantAssets.some(series => series.is_bond),
      priceChanges: priceSeries.length
        ? [
            {
              key: 'price',
              label: 'Price',
              unit: '%' as const,
              value: changes.pricePercentage,
            },
          ]
        : [],
      priceSeries,
      series: [...priceSeries, ...yieldSeries],
      yieldChanges: yieldSeries.length
        ? [
            {
              key: 'yield',
              label: 'Yield',
              unit: 'bp' as const,
              value: changes.yieldBasisPoints,
            },
          ]
        : [],
      yieldSeries,
    };
  }, [data]);

  const options = useMemo<ApexOptions>(() => {
    const priceSeriesNames = chart.priceSeries.map(series => series.name);
    const yieldSeriesNames = chart.yieldSeries.map(series => series.name);
    const hasPriceData = priceSeriesNames.length > 0;
    const hasYieldData = yieldSeriesNames.length > 0;

    const priceAxis: ApexYAxis = {
      forceNiceScale: true,
      seriesName: priceSeriesNames,
      title: { text: 'Price' },
      labels: { formatter: formatPrice },
    };
    const yieldAxis: ApexYAxis = {
      decimalsInFloat: 3,
      forceNiceScale: true,
      opposite: true,
      seriesName: yieldSeriesNames,
      title: { text: 'Ask yield (%)' },
      labels: { formatter: formatYield },
    };

    return {
      chart: {
        animations: { enabled: false },
        parentHeightOffset: 0,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      dataLabels: { enabled: false },
      grid: { padding: { bottom: 0, left: 4, right: 4, top: 0 } },
      legend: {
        fontSize: '11px',
        horizontalAlign: 'left',
        position: 'top',
      },
      markers: { size: 0 },
      stroke: { curve: 'straight', width: 2 },
      tooltip: {
        hideEmptySeries: true,
        shared: true,
        x: { format: 'dd MMM yyyy' },
        y: [
          ...priceSeriesNames.map(() => ({ formatter: formatPrice })),
          ...yieldSeriesNames.map(() => ({ formatter: formatYield })),
        ],
      },
      xaxis: {
        labels: { datetimeUTC: true },
        tickPlacement: 'between',
        tooltip: { enabled: false },
        type: 'datetime',
      },
      yaxis:
        hasPriceData && hasYieldData
          ? [priceAxis, yieldAxis]
          : hasYieldData
            ? yieldAxis
            : priceAxis,
    };
  }, [chart.priceSeries, chart.yieldSeries]);

  const chartable = chart.series.length > 0;
  const unavailable = data && data.status !== 'available';

  return (
    <section className="assessment-inspect-assessment__price-chart">
      <div className="assessment-inspect-assessment__price-chart-heading">
        <div className="assessment-inspect-assessment__price-chart-title">
          <Typography.Text strong>
            {instrumentCode ?? 'Instrument'} EOD prices · {periodLabel}
          </Typography.Text>
          {data?.status === 'available' && (
            <div className="assessment-inspect-assessment__period-change-tags">
              {[...chart.priceChanges, ...chart.yieldChanges].map(change => (
                <PeriodChangeTag change={change} key={change.key} />
              ))}
            </div>
          )}
        </div>
        {data && (
          <Typography.Text type="secondary">
            {formatAssessmentDate(data.start_date)} {'->'}{' '}
            {formatAssessmentDate(data.end_date)}
          </Typography.Text>
        )}
      </div>
      <div className="assessment-inspect-assessment__price-chart-body">
        {loading ? (
          <Spin size="small" />
        ) : error ? (
          <Alert
            description={error}
            message="Unable to load EOD prices"
            showIcon
            type="error"
          />
        ) : unavailable ? (
          <Typography.Text type="secondary">{data.message}</Typography.Text>
        ) : data && chartable ? (
          <>
            {data.message && (
              <Typography.Text
                className="assessment-inspect-assessment__price-chart-note"
                type="secondary"
              >
                {data.message}
              </Typography.Text>
            )}
            <div className="assessment-inspect-assessment__price-chart-canvas">
              <Chart
                height="100%"
                options={options}
                series={chart.series}
                type="line"
              />
            </div>
            {chart.hasBondSeries && chart.yieldSeries.length === 0 && (
              <Typography.Text type="secondary">
                No stored ask_yield observations are available for this bond in
                the selected period.
              </Typography.Text>
            )}
          </>
        ) : data ? (
          <Typography.Text type="secondary">
            MarketHistory rows exist, but none contains a chartable PM quote for
            this period.
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">
            Select an instrument and period to load EOD prices.
          </Typography.Text>
        )}
      </div>
    </section>
  );
};

export default InstrumentPriceChart;
